import { notify } from "@general-backoffice/core";
import type { TaxProfile } from "@investhinks/api-client";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Progress, Spinner } from "reactstrap";

import { useFormHandlers } from "../../../hooks/strapi";
import {
  ActivityStep,
  AgreementStep,
  CompanyInfoStep,
  ConfirmStep,
  DeclarationStep,
  IdentificationStep,
  IRPFStep,
  RiskStep,
  TypeStep,
  VIESStep,
} from "./steps";
import { deriveBackendTaxType } from "./taxProfileConstants";
import { isEUCountry, isNonEUCountry, validateSpanishTaxId } from "./validators";

type TranslationFn = (
  key: string,
  fallback?: string | Record<string, unknown>,
  options?: Record<string, unknown>
) => string;

type TaxProfileData = TaxProfile;

/**
 * Get wizard steps based on referrer type, activity type, and partner status.
 * For SPANISH_AUTONOMO, the IRPF step is only shown for PROFESIONAL activity.
 *
 * Partners (AMBASSADOR, INFLUENCER, DISTRIBUTOR) need additional declaration steps
 * for autofactura compliance. Non-partners (customers) only need basic info for billing.
 */
const getStepsForType = (
  referrerType: string | null | undefined,
  activityType: string | null | undefined,
  isPartner = true
): string[] => {
  const baseSteps = ["type", "identification"];

  switch (referrerType) {
    case "SPANISH_COMPANY":
      // Companies always need agreement (autofactura) if partner, otherwise just confirm
      return isPartner
        ? [...baseSteps, "companyInfo", "agreement", "confirm"]
        : [...baseSteps, "companyInfo", "confirm"];
    case "SPANISH_AUTONOMO":
      // For autónomos, show activity step first, then IRPF only if PROFESIONAL
      if (activityType === "PROFESIONAL") {
        return isPartner
          ? [...baseSteps, "activity", "irpf", "agreement", "confirm"]
          : [...baseSteps, "activity", "confirm"];
      }
      return isPartner ? [...baseSteps, "activity", "agreement", "confirm"] : [...baseSteps, "activity", "confirm"];
    case "SPANISH_PARTICULAR":
      // Partners need declaration + risk + agreement, customers only need basic info
      return isPartner ? [...baseSteps, "declaration", "risk", "agreement", "confirm"] : [...baseSteps, "confirm"];
    case "EU_BUSINESS":
      // For EU Business, VIES validation is inline in the identification step
      return isPartner
        ? [...baseSteps, "companyInfo", "agreement", "confirm"]
        : [...baseSteps, "companyInfo", "confirm"];
    case "EU_PRIVATE":
      // Partners need declaration + agreement, customers only need basic info
      return isPartner ? [...baseSteps, "declaration", "agreement", "confirm"] : [...baseSteps, "confirm"];
    case "NON_EU_BUSINESS":
      return isPartner
        ? [...baseSteps, "companyInfo", "agreement", "confirm"]
        : [...baseSteps, "companyInfo", "confirm"];
    case "NON_EU_PRIVATE":
      // Partners need declaration + agreement, customers only need basic info
      return isPartner ? [...baseSteps, "declaration", "agreement", "confirm"] : [...baseSteps, "confirm"];
    default:
      return ["type"];
  }
};

/**
 * Validate current step data.
 * Returns { valid: boolean, error?: string }
 */
const validateStep = (stepId: string, taxProfile: TaxProfileData | null) => {
  switch (stepId) {
    case "type":
      return { valid: !!taxProfile?.profileType };
    case "identification": {
      // EU Business requires VIES verification in this step
      if (taxProfile?.profileType === "EU_BUSINESS") {
        // For EU Business, we just need VAT number and VIES verification
        // taxIdType is implicitly EU_VAT
        if (!taxProfile?.taxIdNumber) {
          return { valid: false };
        }
        // Must have successful VIES verification
        if (!taxProfile?.viesVerified) {
          return { valid: false };
        }
        return { valid: true };
      }

      // Individual types require name field
      const isIndividualType = ["SPANISH_PARTICULAR", "EU_PRIVATE", "NON_EU_PRIVATE"].includes(
        taxProfile?.profileType || ""
      );

      if (isIndividualType && !taxProfile?.name) {
        return { valid: false };
      }

      // NON_EU and EU_PRIVATE types require country selection
      const requiresCountry = ["NON_EU_BUSINESS", "NON_EU_PRIVATE", "EU_PRIVATE"].includes(
        taxProfile?.profileType || ""
      );

      // Other types require tax ID and address (and country for NON_EU/EU_PRIVATE)
      if (!taxProfile?.taxIdType || !taxProfile?.taxIdNumber || !taxProfile?.fiscalAddress) {
        return { valid: false };
      }
      if (requiresCountry && !taxProfile?.country) {
        return { valid: false };
      }
      // Validate country is appropriate for the profile type
      if (taxProfile?.country) {
        const euTypes = ["EU_PRIVATE", "EU_BUSINESS"];
        const nonEUTypes = ["NON_EU_BUSINESS", "NON_EU_PRIVATE"];
        if (euTypes.includes(taxProfile?.profileType || "") && !isEUCountry(taxProfile.country)) {
          return { valid: false, error: "Please select an EU country (excluding Spain)" };
        }
        if (nonEUTypes.includes(taxProfile?.profileType || "") && !isNonEUCountry(taxProfile.country)) {
          return { valid: false, error: "Please select a country outside the EU" };
        }
      }
      // Validate the tax ID format (for Spanish types only)
      if (taxProfile?.profileType?.startsWith("SPANISH_")) {
        const validation = validateSpanishTaxId(taxProfile.taxIdNumber || "", taxProfile.taxIdType || "");
        return validation;
      }
      return { valid: true };
    }
    case "companyInfo":
      return { valid: !!taxProfile?.name && !!taxProfile?.fiscalAddress };
    case "activity":
      return { valid: !!taxProfile?.activityType && !!taxProfile?.iaeCode };
    case "irpf":
      return {
        valid: taxProfile?.irpfRate != null && (taxProfile?.irpfRate !== 0.07 || !!taxProfile?.altaDate),
      };
    case "vies":
      return { valid: !!taxProfile?.viesVerified };
    case "declaration":
      return {
        valid: !!taxProfile?.particularDeclaration || !!taxProfile?.privateIndividualDeclaration,
      };
    case "risk":
      return { valid: !!taxProfile?.riskAcknowledged };
    case "agreement":
      return { valid: !!taxProfile?.autofacturaAgreement };
    case "confirm":
      return { valid: true };
    default:
      return { valid: false };
  }
};

/**
 * Step content renderer.
 */
interface StepContentProps {
  stepId: string;
  taxProfile: TaxProfileData | null;
  handleInputChange: (name: string, value: unknown) => void;
  handleInputEvent: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onVerifyVIES: (vatNumber: string) => Promise<void>;
  isLoading: boolean;
  validationError?: string;
  t: TranslationFn;
  tProfile: TranslationFn;
  iaeActivityType: string | null;
  setIaeActivityType: (type: string | null) => void;
}

const StepContent = ({
  stepId,
  taxProfile,
  handleInputChange,
  handleInputEvent,
  onVerifyVIES,
  isLoading,
  validationError,
  t,
  tProfile,
  iaeActivityType,
  setIaeActivityType,
}: StepContentProps) => {
  switch (stepId) {
    case "type":
      return <TypeStep taxProfile={taxProfile} handleInputChange={handleInputChange} isLoading={isLoading} t={t} />;
    case "identification":
      return (
        <IdentificationStep
          taxProfile={taxProfile}
          handleInputChange={handleInputChange}
          handleInputEvent={handleInputEvent}
          onVerifyVIES={onVerifyVIES}
          isLoading={isLoading}
          validationError={validationError}
          t={t}
        />
      );
    case "companyInfo":
      return (
        <CompanyInfoStep taxProfile={taxProfile} handleInputEvent={handleInputEvent} isLoading={isLoading} t={t} />
      );
    case "activity":
      return (
        <ActivityStep
          taxProfile={taxProfile}
          handleInputChange={handleInputChange}
          handleInputEvent={handleInputEvent}
          isLoading={isLoading}
          t={t}
          tProfile={tProfile}
          iaeActivityType={iaeActivityType}
          setIaeActivityType={setIaeActivityType}
        />
      );
    case "irpf":
      return (
        <IRPFStep
          taxProfile={taxProfile}
          handleInputChange={handleInputChange}
          handleInputEvent={handleInputEvent}
          isLoading={isLoading}
          t={t}
        />
      );
    case "vies":
      return <VIESStep taxProfile={taxProfile} onVerifyVIES={onVerifyVIES} isLoading={isLoading} t={t} />;
    case "declaration":
      return (
        <DeclarationStep taxProfile={taxProfile} handleInputChange={handleInputChange} isLoading={isLoading} t={t} />
      );
    case "risk":
      return <RiskStep taxProfile={taxProfile} handleInputChange={handleInputChange} isLoading={isLoading} t={t} />;
    case "agreement":
      return (
        <AgreementStep taxProfile={taxProfile} handleInputChange={handleInputChange} isLoading={isLoading} t={t} />
      );
    case "confirm":
      return <ConfirmStep taxProfile={taxProfile} t={t} />;
    default:
      return null;
  }
};

/**
 * OnboardingWizard component.
 * Multi-step wizard for tax profile setup.
 */
interface OnboardingWizardProps {
  isOpen: boolean;
  onClose: () => void;
  taxProfile: TaxProfileData | null;
  setTaxProfile: React.Dispatch<React.SetStateAction<TaxProfileData | null>>;
  onComplete: (profile: TaxProfileData) => Promise<void>;
  onVerifyVIES: (vatNumber: string) => Promise<void>;
  isLoading?: boolean;
  isPartner?: boolean;
}

const OnboardingWizard = ({
  isOpen,
  onClose,
  taxProfile,
  setTaxProfile,
  onComplete,
  onVerifyVIES,
  isLoading: externalLoading,
  isPartner = true,
}: OnboardingWizardProps) => {
  const { t } = useTranslation("billingData", { keyPrefix: "taxProfile.wizard" });
  const { t: tProfile } = useTranslation("billingData", { keyPrefix: "taxProfile" });
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { handleInputChange, handleInputEvent } = useFormHandlers(setTaxProfile);

  // Track the activity type suggested by the selected IAE code
  const [iaeActivityType, setIaeActivityType] = useState<string | null>(null);

  const isLoading = externalLoading || isSubmitting;

  const steps = useMemo(
    () => getStepsForType(taxProfile?.profileType, taxProfile?.activityType, isPartner),
    [taxProfile?.profileType, taxProfile?.activityType, isPartner]
  );

  const progress = useMemo(() => {
    if (steps.length <= 1) return 0;
    return Math.round((currentStep / (steps.length - 1)) * 100);
  }, [currentStep, steps.length]);

  const currentStepId = steps[currentStep];

  const stepValidation = useMemo(() => {
    return validateStep(currentStepId, taxProfile);
  }, [currentStepId, taxProfile]);

  const canGoNext = stepValidation.valid;

  const goNext = useCallback(() => {
    if (currentStep < steps.length - 1) {
      setCurrentStep((prev) => prev + 1);
    }
  }, [currentStep, steps.length]);

  const goBack = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  const handleComplete = useCallback(async () => {
    setIsSubmitting(true);
    try {
      // Derive the actual backend tax type for autónomos
      const backendTaxType = deriveBackendTaxType(taxProfile?.profileType || "", taxProfile?.activityType || null);

      // Compute final profile with derived values
      const finalProfile: TaxProfileData = {
        ...taxProfile,
        profileType: backendTaxType,
        // For EU_BUSINESS, ensure taxIdType is EU_VAT
        ...(backendTaxType === "EU_BUSINESS" && { taxIdType: "EU_VAT" }),
      };

      // Update local state for UI consistency
      setTaxProfile(finalProfile);

      // Pass final profile to onComplete (avoids race condition with setState)
      await onComplete(finalProfile);
    } catch (error: unknown) {
      notify.error((error instanceof Error ? error.message : null) || t("error", "Failed to complete tax profile"));
    } finally {
      setIsSubmitting(false);
    }
  }, [onComplete, setTaxProfile, t, taxProfile]);

  const handleClose = useCallback(() => {
    setCurrentStep(0);
    onClose();
  }, [onClose]);

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg" backdrop="static">
      <ModalHeader toggle={handleClose}>{t("title", "Tax Profile Setup")}</ModalHeader>

      <ModalBody>
        {/* Progress Bar */}
        <div className="mb-4">
          <div className="d-flex justify-content-between mb-2">
            <small className="text-muted">
              {t("step", "Step {{current}} of {{total}}", {
                current: currentStep + 1,
                total: steps.length,
              })}
            </small>
            <small className="text-muted">{progress}%</small>
          </div>
          <Progress value={progress} className="progress-sm" />
        </div>

        {/* Step Content */}
        <StepContent
          stepId={currentStepId}
          taxProfile={taxProfile}
          handleInputChange={handleInputChange}
          handleInputEvent={handleInputEvent}
          onVerifyVIES={onVerifyVIES}
          isLoading={isLoading}
          validationError={stepValidation.error}
          t={t}
          tProfile={tProfile}
          iaeActivityType={iaeActivityType}
          setIaeActivityType={setIaeActivityType}
        />
      </ModalBody>

      <ModalFooter className="d-flex justify-content-between">
        <Button color="secondary" outline onClick={goBack} disabled={currentStep === 0 || isLoading}>
          <i className="fas fa-arrow-left mr-2" />
          {t("back", "Back")}
        </Button>

        {currentStepId === "confirm" ? (
          <Button color="success" onClick={handleComplete} disabled={!canGoNext || isLoading}>
            {isSubmitting ? (
              <>
                <Spinner size="sm" className="mr-2" />
                {t("completing", "Completing...")}
              </>
            ) : (
              <>
                <i className="fas fa-check mr-2" />
                {t("complete", "Complete Setup")}
              </>
            )}
          </Button>
        ) : (
          <Button color="primary" onClick={goNext} disabled={!canGoNext || isLoading}>
            {t("next", "Next")}
            <i className="fas fa-arrow-right ml-2" />
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default OnboardingWizard;
